// Supabase Authentication Service for Kinora Public App
import { supabase } from '../lib/supabase.js';

class AuthService {
  constructor() {
    this.user = null;
    this.session = null;
    this.isInitialized = false;
    
    this.initializeAuth();
  }

  // Initialize auth state from Supabase
  async initializeAuth() {
    try {
      // Get initial session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        this.session = session;
        this.user = session.user;
      }
      
      // Listen for auth changes
      supabase.auth.onAuthStateChange((event, session) => {
        this.session = session;
        this.user = session?.user || null;
        
        if (event === 'SIGNED_OUT') {
          this.user = null;
          this.session = null;
        }
      });
      
    } catch (error) {
      console.error('Failed to initialize auth:', error);
    } finally {
      this.isInitialized = true;
    }
  }

  // Check authentication status
  isAuthenticated() {
    return !!(this.session && this.user);
  }

  // Get current user
  getCurrentUser() {
    return this.user;
  }

  // Get current session
  getCurrentSession() {
    return this.session;
  }

  // Register new user
  async register({ email, password, name }) {
    if (!email || !password) {
      throw new Error('Email and password are required');
    }

    if (password.length < 8) {
      throw new Error('Password must be at least 8 characters long');
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password
    });

    if (error) {
      console.error('Sign up error:', error);
      throw new Error(error.message || 'Sign up failed');
    }

    // Attach friendly metadata when we already have a session.
    if (name && data?.session) {
      const { error: metadataError } = await supabase.auth.updateUser({
        data: {
          full_name: name,
          user_type: 'viewer'
        }
      });

      if (metadataError) {
        console.warn('Unable to persist profile metadata after sign up:', metadataError);
      }
    }

    return {
      user: data.user,
      session: data.session,
      needsConfirmation: !data.session // true if email confirmation is required
    };
  }

  // Login user
  async login(email, password) {
    if (!email || !password) {
      throw new Error('Email and password are required');
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      throw new Error(error.message);
    }

    this.session = data.session;
    this.user = data.user;

    return {
      user: data.user,
      session: data.session
    };
  }

  // Logout user
  async logout() {
    const { error } = await supabase.auth.signOut();
    
    if (error) {
      console.error('Logout error:', error);
      throw new Error(error.message);
    }
    
    this.session = null;
    this.user = null;
  }

  // Reset password
  async resetPassword(email) {
    if (!email) {
      throw new Error('Email is required');
    }

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });

    if (error) {
      throw new Error(error.message);
    }
  }

  // Update password
  async updatePassword(newPassword) {
    if (!newPassword || newPassword.length < 8) {
      throw new Error('Password must be at least 8 characters long');
    }

    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      throw new Error(error.message);
    }
  }

  // Update user profile
  async updateProfile(updates) {
    const { error } = await supabase.auth.updateUser({
      data: updates
    });

    if (error) {
      throw new Error(error.message);
    }

    // Refresh user data
    const { data: { user } } = await supabase.auth.getUser();
    this.user = user;
    
    return user;
  }

  // Get user profile data
  getUserProfile() {
    return this.user?.user_metadata || {};
  }

  // Validate current session
  async validateSession() {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        throw error;
      }
      
      this.session = session;
      this.user = session?.user || null;
      
      return !!session;
    } catch (error) {
      console.error('Session validation failed:', error);
      this.session = null;
      this.user = null;
      return false;
    }
  }

  // Check if email is confirmed
  isEmailConfirmed() {
    return this.user?.email_confirmed_at !== null;
  }

  // Resend confirmation email
  async resendConfirmation(email) {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email
    });

    if (error) {
      throw new Error(error.message);
    }
  }

  // Wait for initialization
  async waitForInit() {
    while (!this.isInitialized) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
